import type { BrandFeature } from "./BrandFeature";
import type { BrandPurchaser } from "./BrandPurchaser";
import { BrandUser } from "./BrandUser";

/**
 * Brand Input Interface - Domain layer
 * Defines the input structure for creating a Brand entity
 */
export interface IBrandProps {
  brandId: number;
  brandKey: string;
  brandName: string;
  brandDisplayName: string | null;
  brandDescription: string | null;
  brandLogoUrl: string | null;
  brandIsActive: boolean;
  brandCreatedAt: string | null;
  brandUpdatedAt: string | null;
  brandDeletedAt: string | null;
  brandCreatedBy: number | null;
  brandUpdatedBy: number | null;
  brandDeletedBy: number | null;
  brandFeaturesCount: number | null;
  brandPurchasersCount: number | null;
  brandFeatures?: BrandFeature[] | null;
  brandPurchasers?: BrandPurchaser[] | null;
  brandUsers?: BrandUser[] | null;
  organizationId?: number | null;
}

/**
 * Brand entity - Domain layer
 * Represents brand data from GraphQL API
 */

export class Brand {
  private readonly _brandId: number;
  private readonly _brandKey: string;
  private readonly _brandName: string;
  private readonly _brandDisplayName: string | null;
  private readonly _brandDescription: string | null;
  private readonly _brandLogoUrl: string | null;
  private readonly _brandIsActive: boolean;
  private readonly _brandCreatedAt: string | null;
  private readonly _brandUpdatedAt: string | null;
  private readonly _brandDeletedAt: string | null;
  private readonly _brandCreatedBy: number | null;
  private readonly _brandUpdatedBy: number | null;
  private readonly _brandDeletedBy: number | null;
  private readonly _brandFeaturesCount: number | null;
  private readonly _brandPurchasersCount: number | null;
  private readonly _brandFeatures?: BrandFeature[] | null;
  private readonly _brandPurchasers?: BrandPurchaser[] | null;
  private readonly _brandUsers?: BrandUser[] | null;
  private readonly _organizationId?: number | null;

  constructor(props: IBrandProps) {
    this._brandId = props.brandId;
    this._brandKey = props.brandKey;
    this._brandName = props.brandName;
    this._brandDisplayName = props.brandDisplayName ?? null;
    this._brandDescription = props.brandDescription;
    this._brandLogoUrl = props.brandLogoUrl;
    this._brandIsActive = props.brandIsActive;
    this._brandCreatedAt = props.brandCreatedAt;
    this._brandUpdatedAt = props.brandUpdatedAt;
    this._brandDeletedAt = props.brandDeletedAt;
    this._brandCreatedBy = props.brandCreatedBy;
    this._brandUpdatedBy = props.brandUpdatedBy;
    this._brandDeletedBy = props.brandDeletedBy;
    this._brandFeaturesCount = props.brandFeaturesCount ?? null;
    this._brandPurchasersCount = props.brandPurchasersCount ?? null;
    this._brandFeatures = props.brandFeatures ?? null;
    this._brandPurchasers = props.brandPurchasers ?? null;
    this._brandUsers = props.brandUsers ?? null;
    this._organizationId = props.organizationId ?? null;
  }
  get brandId(): number {
    return this._brandId;
  }
  get brandKey(): string {
    return this._brandKey;
  }
  get brandName(): string {
    return this._brandName;
  }
  get brandDisplayName(): string | null {
    return this._brandDisplayName;
  }
  get brandDescription(): string | null {
    return this._brandDescription;
  }
  get brandLogoUrl(): string | null {
    return this._brandLogoUrl;
  }
  get brandIsActive(): boolean {
    return this._brandIsActive;
  }
  get brandCreatedAt(): string | null {
    return this._brandCreatedAt;
  }
  get brandUpdatedAt(): string | null {
    return this._brandUpdatedAt;
  }
  get brandDeletedAt(): string | null {
    return this._brandDeletedAt;
  }
  get brandCreatedBy(): number | null {
    return this._brandCreatedBy;
  }
  get brandUpdatedBy(): number | null {
    return this._brandUpdatedBy;
  }
  get brandDeletedBy(): number | null {
    return this._brandDeletedBy;
  }
  get brandFeaturesCount(): number | null {
    return this._brandFeaturesCount ?? null;
  }
  get brandPurchasersCount(): number | null {
    return this._brandPurchasersCount ?? null;
  }
  get brandFeatures(): BrandFeature[] | null {
    return this._brandFeatures ?? null;
  }

  get brandPurchasers(): BrandPurchaser[] | null {
    return this._brandPurchasers ?? null;
  }

  get brandUsers(): BrandUser[] | null {
    return this._brandUsers ?? null;
  }
  get organizationId(): number | null {
    return this._organizationId ?? null;
  }

  static toProps(brand: Brand): IBrandProps {
    return {
      brandId: brand.brandId,
      brandKey: brand.brandKey,
      brandName: brand.brandName,
      brandDisplayName: brand.brandDisplayName,
      brandDescription: brand.brandDescription,
      brandLogoUrl: brand.brandLogoUrl,
      brandIsActive: brand.brandIsActive,
      brandCreatedAt: brand.brandCreatedAt,
      brandUpdatedAt: brand.brandUpdatedAt,
      brandDeletedAt: brand.brandDeletedAt,
      brandCreatedBy: brand.brandCreatedBy,
      brandUpdatedBy: brand.brandUpdatedBy,
      brandDeletedBy: brand.brandDeletedBy,
      brandFeaturesCount: brand.brandFeaturesCount,
      brandPurchasersCount: brand.brandPurchasersCount,
      brandFeatures: brand.brandFeatures,
      brandPurchasers: brand.brandPurchasers,
      brandUsers: brand.brandUsers,
      organizationId: brand.organizationId,
    };
  }

  static withProps(brand: Brand, overrides: Partial<IBrandProps>): Brand {
    return new Brand({ ...Brand.toProps(brand), ...overrides });
  }
}
